import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput, TextareaInput } from "@/components/formfields";
import type { AwardDetail, AwardPayload } from "@/interface/award";
import { useCreateAward, useUpdateAward } from "../hooks/useAwards";
import { useProfileDetail } from "../hooks/useProfile";

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const awardSchema = z
  .object({
    award_title: z
      .string()
      .trim()
      .min(1, "Award title is required")
      .max(255, "Award title cannot exceed 255 characters"),
    issuer: z
      .string()
      .trim()
      .min(1, "Issuer / Organization is required")
      .max(255, "Issuer cannot exceed 255 characters"),
    award_date: z.string().trim().min(1, "Award date is required"),
    description: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    const todayStr = getTodayDateString();
    if (data.award_date && data.award_date > todayStr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Award date cannot be in the future",
        path: ["award_date"],
      });
    }
  });

export type AwardFormValues = z.infer<typeof awardSchema>;

interface AwardFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: AwardDetail | null;
  profileId?: number | string;
}

export const AwardFormModal: React.FC<AwardFormModalProps> = ({
  isOpen,
  onClose,
  editData,
  profileId,
}) => {
  const createMutation = useCreateAward();
  const updateMutation = useUpdateAward();
  const { data: profileResponse } = useProfileDetail();

  const selfId = profileResponse?.data?.id;
  const targetUserId = profileId ?? selfId ?? editData?.user_profile;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<AwardFormValues>({
    resolver: zodResolver(awardSchema),
    defaultValues: {
      award_title: "",
      issuer: "",
      award_date: "",
      description: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        reset({
          award_title: editData.award_title || "",
          issuer: editData.issuer || "",
          award_date: editData.award_date || "",
          description: editData.description || "",
        });
      } else {
        reset({
          award_title: "",
          issuer: "",
          award_date: "",
          description: "",
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: AwardFormValues) => {
    try {
      const payload: AwardPayload = {
        award_title: values.award_title,
        issuer: values.issuer,
        award_date: values.award_date,
        description: values.description || null,
        user_profile: targetUserId,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Handled by query mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>
            {editData ? "Edit Award / Achievement" : "Add Award / Achievement"}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 dark:bg-slate-900/50">
            <TextInput
              control={control}
              name="award_title"
              label="Award Title"
              placeholder="e.g. Employee of the Year"
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="issuer"
                label="Issuer / Organization"
                placeholder="e.g. Acme Corp"
                required
              />
              <TextInput
                control={control}
                name="award_date"
                label="Award Date"
                type="date"
                max={getTodayDateString()}
                required
              />
            </div>

            <TextareaInput
              control={control}
              name="description"
              label="Description"
              placeholder="Details about the achievement..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
                ? "Saving..."
                : editData
                  ? "Save Changes"
                  : "Add Award"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AwardFormModal;
